<script setup>
// KISOFT: area de conteudo da Gestao (topo do painel + pagina ativa), a direita da barra.
// Fica escondida quando a view e 'atendimento' (ai aparecem as conversas do Chatwoot).
import { computed, nextTick, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import GestaoMeta from './GestaoMeta.vue';

const props = defineProps({
  view: { type: String, default: 'home' },
  // barra da Gestao recolhida (64px) -> o conteudo encosta nela
  recolhida: { type: Boolean, default: false },
});

const currentUser = useMapGetter('getCurrentUser');
const selectedChat = useMapGetter('getSelectedChat');
// KISOFT: identidade segura — passa o access_token do Chatwoot; o painel verifica na API e loga
// como o membro vinculado (cw_email). Cada pagina do painel vira iframe embed com esse token.
const EMB = 'emb_9Kx72Qm4Zp';
const embSrc = view =>
  `https://kisoft.com.br/tarefass/?view=${view}&embed=${EMB}&cwtok=${encodeURIComponent((currentUser.value && currentUser.value.access_token) || '')}`;
const assinSrc = computed(() => {
  // manda ID e telefone do contato da conversa; o iframe (painel) resolve e fixa no topo (destacado),
  // mantendo a lista inteira — nao e uma busca/filtro.
  const base = embSrc('assinaturas');
  const c = selectedChat.value;
  const s = c && c.meta && c.meta.sender;
  const parts = [];
  if (s) {
    if (s.identifier) parts.push('kwid=' + encodeURIComponent(s.identifier));
    if (s.phone_number) parts.push('kwph=' + encodeURIComponent(s.phone_number));
  }
  return parts.length ? base + '&' + parts.join('&') : base;
});
const nome = computed(() => (currentUser.value && (currentUser.value.name || currentUser.value.available_name)) || 'Usuário');
const admin = computed(() => (currentUser.value && currentUser.value.role) === 'administrator');
const ini = computed(() => {
  const p = nome.value.trim().split(/\s+/);
  return ((p[0] || '')[0] || '' ) + (p.length > 1 ? (p[p.length - 1][0] || '') : '');
});

// mesmos titulos/crumbs do render_topbar do painel
const T = {
  home: ['Meta', 'Faturamento do mês x meta'],
  crm: ['CRM — Funis', 'Roteamento de clientes por etapa (betsdb)'],
  exec: ['Visão Executiva', 'Painel de gestão da empresa'],
  areas: ['Áreas da Empresa', 'Desempenho e iniciativas de cada área'],
  assinaturas: ['Assinaturas', 'Base de clientes, vencimentos e churn por produto'],
  projetos: ['Projetos', 'Organize e acompanhe todos os seus projetos em um só lugar.'],
  list: ['Tarefas', 'Greensurebet › Demandas'],
  minhas: ['Minhas Tarefas', 'Tarefas atribuídas a você'],
  chat: ['Chat da equipe', 'Conversa interna do time'],
  metricas: ['Métricas', 'Indicadores do CRM: receita, aquisição, retenção e base de clientes'],
  calendar: ['Calendário', 'Suas tarefas na semana'],
  ponto: ['Controle de ponto', 'Registre entrada, pausa e saída da equipe'],
  reunioes: ['Reuniões', 'Agende e organize a pauta de cada uma'],
  editorial: ['Calendário Editorial', 'Central de operações de conteúdo'],
  pagamentos: ['Auditoria de Pagamentos', 'Estudo e validação dos pagamentos (betsdb)'],
  chamados: ['Chamados', 'Fila de bugs, melhorias e pendências'],
  gcfg: ['Configurações', 'Ajustes gerais da gestão'],
  config: ['Equipe', 'Usuários e permissões'],
  approvals: ['Aprovações', 'Tarefas aguardando aprovação'],
  history: ['Atividades', 'Histórico de atividades recentes'],
};
const ttl = computed(() => (T[props.view] || ['Kisoft', ''])[0]);
const crb = computed(() => (T[props.view] || ['Kisoft', ''])[1]);
// Minhas Tarefas = mesma pagina de Tarefas (view=list) filtrada pelo usuario da sessao (mine=1).
const listSrc = computed(() => embSrc('list'));
const minhasSrc = computed(() => embSrc('list') + '&mine=1');
// exec/chamados (paginas render_dash) mostram o TOPO NATIVO do painel dentro do iframe (emb_top=1)
// -> ficam identicas ao painel; a barra replicada do Chatwoot fica escondida nessas views.
const NATIVE_TOP = ['exec', 'chamados', 'organo', 'chat', 'metricas', 'pagamentos', 'areas', 'reunioes'];
const nativeTop = computed(() => NATIVE_TOP.includes(props.view));
const execSrc = computed(() => embSrc('exec') + '&emb_top=1');
const chamadosSrc = computed(() => embSrc('chamados') + '&emb_top=1');
// Organograma: editor completo do painel; permissao POR MAPA (dono/admin/compartilhados) e do painel.
const organoSrc = computed(() => embSrc('organo') + '&emb_top=1');
// Chat da equipe: pagina do painel (poll + marca lido); o aviso fora dela e da GestaoSidebar.
const chatSrc = computed(() => embSrc('chat') + '&emb_top=1');
// Metricas: abas internas navegam dentro do iframe (o painel mantem o embed pelo Sec-Fetch-Dest)
const metricasSrc = computed(() => embSrc('metricas') + '&emb_top=1');
// Auditoria de Pagamentos: pagina pronta do painel (?view=pagamentos), topo nativo
const pagamentosSrc = computed(() => embSrc('pagamentos') + '&emb_top=1');
// Areas / Reunioes: paginas render_dash do painel (topo nativo). Atividades (history) e Equipe (config):
// shell de tarefas do painel (sem topo nativo -> usa o topo do Chatwoot, igual Tarefas/Calendario).
const areasSrc = computed(() => embSrc('areas') + '&emb_top=1');
const reunioesSrc = computed(() => embSrc('reunioes') + '&emb_top=1');
const historySrc = computed(() => embSrc('history'));
const configSrc = computed(() => embSrc('config'));
// KISOFT: botao "Ideia" do topo — mesmo comportamento do painel (modal -> tarefa no projeto Triagem).
// Aqui o painel esta em outro dominio, entao grava pelo endpoint ?idea_nova=1 (CORS + cwtok).
const ideaAberta = ref(false);
const ideaTxt = ref('');
const ideaSalvando = ref(false);
const ideaToast = ref('');
const ideaInput = ref(null);
let ideaToastT = null;
const ideaOpen = () => {
  ideaTxt.value = '';
  ideaAberta.value = true;
  nextTick(() => ideaInput.value && ideaInput.value.focus());
};
const ideaClose = () => { ideaAberta.value = false; };
const ideaAviso = msg => {
  ideaToast.value = msg;
  clearTimeout(ideaToastT);
  ideaToastT = setTimeout(() => { ideaToast.value = ''; }, 3200);
};
const ideaSalvar = async () => {
  const v = ideaTxt.value.trim();
  if (!v || ideaSalvando.value) { if (!v && ideaInput.value) ideaInput.value.focus(); return; }
  ideaSalvando.value = true;
  try {
    const tok = (currentUser.value && currentUser.value.access_token) || '';
    const fd = new FormData();
    fd.append('texto', v);
    const r = await fetch(
      `https://kisoft.com.br/tarefass/index.php?idea_nova=1&cwtok=${encodeURIComponent(tok)}`,
      { method: 'POST', body: fd, credentials: 'include', headers: { 'X-Requested-With': 'XMLHttpRequest' } }
    );
    const j = await r.json();
    if (j && j.ok) { ideaClose(); ideaAviso('💡 Ideia salva no projeto Triagem!'); }
    else { ideaAviso((j && j.msg) || 'Falha ao salvar a ideia.'); }
  } catch (e) {
    ideaAviso('Falha de conexão com o painel.');
  } finally {
    ideaSalvando.value = false;
  }
};

const migrada = computed(() =>
  ['home', 'crm', 'assinaturas', 'calendar', 'exec', 'list', 'minhas', 'projetos', 'chamados', 'organo', 'chat', 'metricas', 'pagamentos', 'areas', 'reunioes', 'history', 'config'].includes(props.view)
);
</script>

<template>
  <div class="kg-cwrap" :style="{ left: (recolhida ? 64 : 220) + 'px' }">
    <div v-if="!nativeTop" class="top">
      <div>
        <h1>{{ ttl }}</h1>
        <div v-if="crb" class="crumb">{{ crb }}</div>
      </div>
      <div class="grow" />
      <form class="tsearch" @submit.prevent>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7" /><path d="M20.5 20.5 17 17" /></svg>
        <input placeholder="Buscar em tudo…" autocomplete="off" >
      </form>
      <button type="button" class="idea-top" title="Anotar uma ideia (vai para o projeto Triagem)" @click="ideaOpen">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1v.2h6v-.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2Z" /></svg><span>Ideia</span>
      </button>
      <button type="button" class="bell" title="Notificações">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" /></svg>
      </button>
      <div class="topuser">
        <span class="av">{{ ini }}</span>
        <span class="tu-name">{{ nome }}<span v-if="admin" class="adm">admin</span></span>
      </div>
      <a class="tbtn" href="#" @click.prevent>Sair</a>
    </div>
    <div class="kg-cbody" :class="{ flush: migrada }">
      <GestaoMeta v-if="view === 'home'" />
      <!-- KISOFT: paginas pesadas = iframe da pagina real do painel (embed, identidade por cwtok) -->
      <iframe v-else-if="view === 'crm'" class="kg-frame" :src="embSrc('crm')" />
      <iframe v-else-if="view === 'assinaturas'" class="kg-frame" :src="assinSrc" />
      <iframe v-else-if="view === 'calendar'" class="kg-frame" :src="embSrc('calendar')" />
      <iframe v-else-if="view === 'exec'" class="kg-frame" :src="execSrc" />
      <iframe v-else-if="view === 'list'" class="kg-frame" :src="listSrc" />
      <iframe v-else-if="view === 'minhas'" class="kg-frame" :src="minhasSrc" />
      <iframe v-else-if="view === 'projetos'" class="kg-frame" :src="embSrc('projetos')" />
      <iframe v-else-if="view === 'chamados'" class="kg-frame" :src="chamadosSrc" />
      <iframe v-else-if="view === 'organo'" class="kg-frame" :src="organoSrc" />
      <iframe v-else-if="view === 'chat'" class="kg-frame" :src="chatSrc" />
      <iframe v-else-if="view === 'metricas'" class="kg-frame" :src="metricasSrc" />
      <iframe v-else-if="view === 'pagamentos'" class="kg-frame" :src="pagamentosSrc" />
      <iframe v-else-if="view === 'areas'" class="kg-frame" :src="areasSrc" />
      <iframe v-else-if="view === 'reunioes'" class="kg-frame" :src="reunioesSrc" />
      <iframe v-else-if="view === 'history'" class="kg-frame" :src="historySrc" />
      <iframe v-else-if="view === 'config'" class="kg-frame" :src="configSrc" />
      <div v-else class="kg-soon">
        <div class="kg-soon-ic">🏗️</div>
        <h2>{{ ttl }}</h2>
        <p>Página em migração para o Chatwoot — em breve, no mesmo padrão do painel.</p>
      </div>
    </div>
    <!-- KISOFT: anotar ideia bruta (igual ao painel) -> tarefa no projeto Triagem -->
    <div
      v-if="ideaAberta"
      class="idea-bg"
      @click.self="ideaClose"
      @keydown.esc="ideaClose"
    >
      <div class="idea-modal">
        <div class="idea-head">
          <span class="idea-ic">💡</span><b>Anotar ideia</b>
          <button type="button" class="idea-x" @click="ideaClose">✕</button>
        </div>
        <div class="idea-body">
          <textarea
            ref="ideaInput"
            v-model="ideaTxt"
            placeholder="Escreva sua ideia bruta…"
            @keydown.ctrl.enter.prevent="ideaSalvar"
            @keydown.meta.enter.prevent="ideaSalvar"
          />
          <div class="idea-note">📁 Esta ideia vai para o projeto <b>Triagem</b> (você organiza depois).</div>
        </div>
        <div class="idea-foot">
          <button type="button" class="idea-btn ghost" @click="ideaClose">Cancelar</button>
          <button type="button" class="idea-btn prim" :disabled="ideaSalvando" @click="ideaSalvar">
            {{ ideaSalvando ? 'Salvando…' : 'Salvar ideia' }}
          </button>
        </div>
      </div>
    </div>
    <div v-if="ideaToast" class="idea-toast">{{ ideaToast }}</div>
  </div>
</template>

<style>
.kg-cwrap{position:fixed;top:0;bottom:0;right:0;left:220px;z-index:55;display:flex;flex-direction:column;transition:left .22s ease;
  background:var(--bg);color:var(--ink);font-family:'Inter',system-ui,-apple-system,Arial,sans-serif;font-size:13px;
  --bg:#f1f3f7;--panel:#ffffff;--line:#e6e8ee;--ink:#1d2230;--mut:#6d7480;--accent:#f2662e;--soft:rgba(242,102,46,.14)}
.kg-cwrap .top{display:flex;align-items:center;gap:12px;padding:13px 24px;border-bottom:1px solid var(--line);background:var(--panel);flex-shrink:0}
.kg-cwrap .top h1{margin:0;font-size:19px;font-weight:700;color:var(--ink);line-height:1.15}
.kg-cwrap .top .crumb{font-size:11.5px;color:var(--mut);margin-top:2px}
.kg-cwrap .top .grow{flex:1}
.kg-cwrap .tsearch{position:relative;display:flex;align-items:center;gap:8px;background:#f1f3f6;border:1px solid var(--line);border-radius:9px;padding:0 11px;height:38px;flex:0 1 300px;min-width:150px}
.kg-cwrap .tsearch svg{width:15px;height:15px;color:var(--mut);flex-shrink:0}
.kg-cwrap .tsearch input{border:0;background:transparent;outline:0;color:var(--ink);font-size:12.5px;width:100%;font-family:inherit}
.kg-cwrap .idea-top{display:inline-flex;align-items:center;gap:7px;height:38px;padding:0 14px;border:1px solid var(--line);background:var(--panel);color:var(--ink);border-radius:9px;font-size:12.5px;font-weight:700;cursor:pointer;white-space:nowrap;font-family:inherit}
.kg-cwrap .idea-top svg{width:16px;height:16px;color:#eab308}
.kg-cwrap .idea-top:hover{border-color:#eab308;color:#eab308}
.kg-cwrap .idea-bg{position:fixed;inset:0;background:rgba(5,8,13,.55);display:flex;align-items:center;justify-content:center;z-index:400;padding:20px}
.kg-cwrap .idea-modal{width:520px;max-width:100%;background:var(--panel);border:1px solid var(--line);border-radius:14px;box-shadow:0 24px 70px rgba(0,0,0,.4);overflow:hidden}
.kg-cwrap .idea-head{display:flex;align-items:center;gap:10px;padding:15px 20px;border-bottom:1px solid var(--line)}
.kg-cwrap .idea-ic{font-size:18px}
.kg-cwrap .idea-head b{flex:1;font-size:15.5px;font-weight:700;color:var(--ink)}
.kg-cwrap .idea-x{background:none;border:0;color:var(--mut);font-size:17px;cursor:pointer;line-height:1}
.kg-cwrap .idea-x:hover{color:var(--ink)}
.kg-cwrap .idea-body{padding:16px 20px}
.kg-cwrap .idea-body textarea{width:100%;box-sizing:border-box;background:#f1f3f6;border:1.5px solid var(--line);border-radius:10px;padding:12px 13px;color:var(--ink);font-size:14px;font-family:inherit;outline:0;resize:vertical;min-height:96px;line-height:1.45}
.kg-cwrap .idea-body textarea:focus{border-color:var(--accent)}
.kg-cwrap .idea-note{margin-top:12px;font-size:12.5px;color:var(--mut);background:#f1f3f6;border:1px solid var(--line);border-radius:9px;padding:9px 12px}
.kg-cwrap .idea-note b{color:var(--ink)}
.kg-cwrap .idea-foot{display:flex;justify-content:flex-end;gap:10px;padding:14px 20px 18px}
.kg-cwrap .idea-btn{font-size:13.5px;font-weight:700;padding:10px 18px;border-radius:9px;cursor:pointer;border:1px solid transparent;font-family:inherit}
.kg-cwrap .idea-btn.ghost{background:transparent;border-color:var(--line);color:var(--ink)}
.kg-cwrap .idea-btn.ghost:hover{border-color:var(--mut)}
.kg-cwrap .idea-btn.prim{background:var(--accent);color:#fff;box-shadow:0 4px 14px rgba(242,102,46,.3)}
.kg-cwrap .idea-btn.prim:hover{filter:brightness(1.06)}
.kg-cwrap .idea-btn.prim:disabled{opacity:.6;cursor:default}
.kg-cwrap .idea-toast{position:fixed;top:14px;left:50%;transform:translateX(-50%);z-index:500;background:#1c2132;color:#fff;border-radius:11px;box-shadow:0 14px 40px rgba(0,0,0,.35);padding:12px 20px;font-size:13.5px;font-weight:700;pointer-events:none;animation:kg-idea-in .3s ease}
@keyframes kg-idea-in{from{opacity:0;transform:translateX(-50%) translateY(-24px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}
.kg-cwrap .bell{position:relative;width:38px;height:38px;border-radius:9px;background:var(--panel);border:1px solid var(--line);display:flex;align-items:center;justify-content:center;color:var(--mut);cursor:pointer;flex-shrink:0}
.kg-cwrap .bell svg{width:18px;height:18px}
.kg-cwrap .bell:hover{color:var(--ink);border-color:var(--accent)}
.kg-cwrap .tbtn{height:38px;padding:0 13px;border:1px solid var(--line);border-radius:9px;background:var(--panel);color:var(--mut);font-size:12.5px;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:6px;text-decoration:none;white-space:nowrap}
.kg-cwrap .tbtn:hover{color:var(--ink);border-color:var(--accent)}
.kg-cwrap .topuser{display:flex;align-items:center;gap:9px;padding:3px 11px 3px 3px;border-radius:30px;border:1px solid var(--line);background:var(--panel)}
.kg-cwrap .topuser .av{width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:11.5px;background:linear-gradient(135deg,#f2662e,#ff9f1a)}
.kg-cwrap .topuser .tu-name{font-size:12.5px;font-weight:700;color:var(--ink);white-space:nowrap}
.kg-cwrap .topuser .adm{font-size:9px;font-weight:700;color:var(--accent);background:var(--soft);border-radius:20px;padding:1px 6px;margin-left:5px;text-transform:uppercase}
.kg-cbody{flex:1;min-height:0;overflow:auto}
.kg-cbody.flush{overflow:hidden}
.kg-frame{width:100%;height:100%;border:0;display:block;background:#f1f3f7}
.kg-soon{max-width:520px;margin:8vh auto 0;text-align:center;padding:0 20px}
.kg-soon-ic{font-size:44px;margin-bottom:14px}
.kg-soon h2{font-size:20px;margin:0 0 8px;color:var(--ink)}
.kg-soon p{color:var(--mut);font-size:14px;line-height:1.55;margin:0}
</style>
